<template>
  <div>
    <div class="flex flex-col items-center text-center py-8 w-full">
      <h3 class="text-base font-medium text-surface-9 mb-1">Monetize with Paywall</h3>
      <p class="text-sm text-surface-7 mb-4 max-w-sm">
        Charge for access or collect viewer emails before playback. Pricing, access and payouts are managed on the Paywall page.
      </p>
      <FormButton type="main" size="md" to="/paywall/v2">Open Paywall</FormButton>
    </div>
  </div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from "lodash";
import moment from "moment";
import { mapGetters } from 'vuex'
import Spinner from "@/components/ui/Spinner.vue"
import CountryCurrencyDropdown from "@/components/ui/CountryCurrencyDropdown.vue"
import PaywallService from "@/services/PaywallService"
import PaywallSubscriptionDetails from "@/components/ui/PaywallSubscriptionDetails"
import SubscriptionService from "@/services/SubscriptionService"
import StreamService from "@/services/StreamService"
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import Alert from '@/components/Atoms/Alert.vue';

export default {
  name: "StreamManagePaywall",
  components: {
		Tooltip,
    Alert,
    FormButton,
    FormInput,
    Spinner,
    PaywallSubscriptionDetails,
    CountryCurrencyDropdown,
    ConfirmModal
	},
  props: {
    stream: {
      type: Object,
      required: true,
      validator(v) {
        return Object.prototype.hasOwnProperty.call(v, "_id");
      },
    },
  },
  async mounted() {
    await this.setupPaywall();
  },
  watch: {
    selectedPrice: function (val) {
      const newPrice = parseInt(val);
      if (newPrice <= 0) {
        this.selectedType = "collect_emails";
        return;
      }

      this.selectedType = this.selectedType;
    },
    selectedType: function (val) {
      if (val === "collect_emails") {
        this.selectedPrice = 0;
      }
      if (val === "payperview" || val === 'subscription') {
        this.selectedPrice = this.initialPrice > 0 ? this.initialPrice : 1;
      }
    },
  },
  data() {
    return {
      paywallVersion: 'v2',
      processing: false,
      trialError: false,

      stripeOauthLink: null,
      stripeConnectProcessing: false,
      stripeConnectStatusChecker: null,
      streamPaywall: null,

      paywallDetailsActive: false,
      stripeConnectLinkError: false,

      paymentTypes: [
       
        {
          text: "Pay per view",
          value: "payperview",
        },
        {
          text: "Collect emails only",
          value: "collect_emails",
        },
      ],

      subPeriod: [
      {
          text: "Day",
          value: "day",
        },
        {
          text: "Week",
          value: "week",
        },
        {
          text: "Month",
          value: "month",
        },
        {
          text: "Year",
          value: "year",
        },
      ],
      initialSubPeriod: 'year',

      selectedRecurrence: "year",
      selectedType: "payperview",
      initialType: 'payperview',

      initialCurrency: "usd",
      selectedCurrency: "usd",

      initialPrice: 1,
      selectedPrice: 1,

      tempLinks: [],
      tempLinkDateTime: null,
      tempLinkMaxDateTime: null,
      tempLinkMinDateTime: null,
      showTempLinkDatePicker: false,
      tempLinkProcessing: false,
    };
  },
  computed: {
    ...mapGetters({
      loadingStatus: "Ui/loadingStatus",
      baseSubscription: 'User/baseSubscription',
			addonSubscriptions: 'User/addonSubscriptions'
		}),
    subscriptionPaywallFeature() {
      // allow paywall for VOD if user has active and not trial all in one sub
      const subMeta = this.$store.state.User.subscriptionMeta.live
      if (subMeta.enabled && !subMeta.trial) {
        return true
      }
      
      return this.$store.state.User.aggregatedDefinition[this.stream.type].paywall
    },
    isInitialPriceChanged() {
      if (
        parseInt(this.selectedPrice) >= 0 &&
        parseInt(this.selectedPrice) !== parseInt(this.initialPrice)
      ) {
        return true;
      }
      return false;
    },
    initialValueChanged() {
      if (this.initialType !== this.selectedType) {
        return true
      }

      if (
        parseInt(this.selectedPrice) >= 0 &&
        parseInt(this.selectedPrice) !== parseInt(this.initialPrice)
      ) {
        return true;
      }

      if (this.initialCurrency.toLowerCase() !== this.selectedCurrency.toLowerCase()) {
        return true;
      }

      if (this.initialSubPeriod !== this.selectedRecurrence) {
        return true
      }

      return false
    },
    tempLinksFiltered() {
      const now = new Date().getTime();
      const filtered = _.filter(this.tempLinks, (item) => {
        return new Date(item.expireAt).getTime() > now;
      });

      return _.sortBy(filtered, function (d) {
        return new Date(d.expireAt);
      });
    },
    baseLiveSubscription() {
      const subs = this.$store.state.User.subscriptions.map((sub) =>
        _.assign({ category: "restream" }, sub)
      );
      return subs.find((sub) => sub.category && sub.category.includes("live"));
    },
    paymentTypesFilter() {
      const baseSub = this.baseLiveSubscription;

      const pwAccess = _.get(baseSub, [
        "package",
        "definition",
        "paywall:full-bundle",
      ]);
      if (pwAccess || pwAccess === undefined) {
        return this.paymentTypes;
      }

      return _.filter(this.paymentTypes, (i) => i.value === "collect_emails");
    },
    activeTeam() {
      return this.$store.state.User.activatedTeam;
    },
    memberPermission() {
      return this.activeTeam && this.$store.state.User.memberPermission;
    },
    userEmail() {
      return this.$store.state.User.email;
    },
    userloaded() {
      return this.$store.state.User.userloaded;
    },
  },
  methods: {
    async onPaywallActivation() {
      if (this.selectedType === 'collect_emails') {
        await this.activateStreamPaywall()
        return
      }

      showModal('activate-paywall');
    },
    onCurrencySelected(curr) {
      this.selectedCurrency = curr.code.toLowerCase()
    },
    checkPaywallAccess() {
      return this.activeTeam && this.memberPermission && this.memberPermission.paywall;
    },
    async liveSubscriptionTrial() {
      const subs = { subscription: this.baseSubscription, addonSubscriptions: this.addonSubscriptions};
      const addonSubs = subs.addonSubscriptions.map((sub) =>
        _.assign({ category: "restream" }, sub)
      );
      const baseSub = addonSubs.find(
        (sub) => sub.category && sub.category.includes("live")
      );
      return !baseSub.enabled;
    },
    async generateTempLink() {
      this.tempLinkProcessing = true;
      try {
        const expireAt = new Date(this.tempLinkDateTime).getTime();
        const expireMin = Math.round((expireAt - new Date().getTime()) / 60000);

        const resp = await PaywallService.createTemporaryLink(
          this.stream._id,
          expireAt,
          expireMin
        );
        this.tempLinks.push({
          url: resp.link,
          expireAt,
        });

        this.showTempLinkDatePicker = null;
        this.tempLinkDateTime = null;
        this.$notify({
          group: "success",
          text: "Temporary link generated",
        });
      } catch (err) {
        console.error("err", err);
        this.$notify({
          group: "error",
          text: err.message || "Generate temporary link failed",
        });
      }
      this.tempLinkProcessing = false;
    },
    setRangesTempLinkDateTime() {
      this.tempLinkMaxDateTime = new Date(
        Date.now() + 1 * 1000 * 60 * 60 * 24 * 10
      ).toISOString();
      this.tempLinkMinDateTime = new Date(
        Date.now() + 180 * 1000
      ).toISOString();
    },
    clipboardCopy(text) {
      try {
        if (text instanceof Function) text = text();

        this.$copyText(text);
        this.$notify({ group: "info", text: "Copied to clipboard" });
      } catch (e) { }
    },
    onStreamPaywallPriceChanged(price) {
      this.initialPrice = price;
      this.streamPaywall.price = price;
      this.selectedPrice = 0;
    },
    onStreamPaywallChangesCancel() {
      this.selectedPrice = this.initialPrice;
      this.selectedCurrency = this.initialCurrency;
    },
    async updateStreamPaywall() {
      try {
        const payload = {
          title: this.stream.name,
          recurring: this.selectedType === 'subscription' ? 1 : 0,
          currency: this.selectedCurrency,
          price: this.selectedPrice,
          period: this.selectedRecurrence,
        };

        await PaywallService.updateStreamPaywall(this.stream._id, payload);

        this.initialPrice = this.selectedPrice;
        this.initialCurrency = this.selectedCurrency;
        this.initialSubPeriod = this.selectedRecurrence
        this.initialType = this.selectedType
        this.streamPaywall.price = this.selectedPrice;
        this.streamPaywall.currency = this.selectedCurrency;

        this.$notify({
          group: "success",
          text: "Stream paywall updated.",
        });
      } catch (err) {
        this.$notify({
          group: "error",
          text: err.message || "Stream paywall update failed",
        });
      }
      return;
    },
    async activateStreamPaywall() {
      try {
        const payload = {
          url: `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/${this.stream.key}?paywall=1`,
          title: this.stream.name,
          recurring: this.selectedType === 'subscription' ? 1 : 0,
          currency: this.selectedCurrency,
          price: this.selectedPrice,
          period: this.selectedRecurrence,
        };

        if (this.stream.type === 'vod') {
          payload.url = `${process.env.VUE_APP_PLAYER_APP_BASE_URL}/vod/playlist/${this.stream._id}?paywall=1`;
        }

        const pwObj = await PaywallService.activateStreamPaywall(
          this.stream._id,
          payload
        );
        this.selectPaywallSubscription(pwObj);

        this.$notify({
          group: "success",
          text: "Stream paywall activated",
        });
      } catch (err) {
        this.$notify({
          group: "error",
          text: err.message || "Stream paywall activation failed",
        });
      }
      return;
    },
    async setupPaywall() {
      // this.$store.dispatch('Ui/setLoadingStatus', true);
      this.processing = true;
      try {
        const pwMaster = await PaywallService.initPaywall();
        if (pwMaster.oauthLink) {
          // stripe not linked
          this.stripeOauthLink = pwMaster.oauthLink;
          this.processing = false;
          // this.$store.dispatch('Ui/setLoadingStatus', false);
          return;
        }

        // stripe attached update each time poster and title
        const streamMeta = await StreamService.getStreamMetadata(
          this.stream._id
        );
        if (streamMeta.embedPoster) {
          const poster = `https://assets.castr.io/embedImages/${streamMeta.embedPoster}`;
          const payload = { wallpaper_url: poster, title: this.stream.name };
          await PaywallService.updateStreamPaywall(this.stream._id, payload);
        }

        const pwStream = await PaywallService.getStreamPaywall(this.stream._id);
        const pwObj = { ...pwStream, master: pwMaster };
        this.selectPaywallSubscription(pwObj);

        const resp = await PaywallService.getTemporaryLinks(this.stream._id);
        this.tempLinks = resp.links;
      } catch (err) {
        if (err.toString().includes("stream not found")) {
          this.processing = false;
          // this.$store.dispatch('Ui/setLoadingStatus', false);
          return;
        }

        this.$notify({
          group: "error",
          text: err.message || "paywall initialization failed",
        });
      }
      this.processing = false;
      // this.$store.dispatch('Ui/setLoadingStatus', false);
    },
    selectPaywallSubscription(pwObj) {
      this.streamPaywall = pwObj;

      this.selectedCurrency = pwObj.currency || "usd";
      this.initialCurrency = pwObj.currency || "usd";
      this.selectedPrice = pwObj.price;
      this.initialPrice = pwObj.price;
      this.initialRecurring = pwObj.recurring;
      this.initialSubPeriod = pwObj.period

      this.selectedType =
        parseInt(pwObj.price) === 0 ? "collect_emails" : "payperview";

      if (pwObj.recurring === 1) {
        this.selectedType = 'subscription'
      }

      this.initialType = this.selectedType
    },
    onStripeConnectClick() {
      this.stripeConnectLinkError = false;
      this.stripeConnectProcessing = true;
      this.monitorStripeLinkingStatus();
    },
    monitorStripeLinkingStatus() {
      this.stripeConnectStatusChecker = setInterval(async () => {
        try {
          const resp = await PaywallService.stripeLinkingStatus();

          if (resp.status === "active") {
            // init stream
            this.stripeOauthLink = null;
            this.stripeConnectLinkError = false;
            clearInterval(this.stripeConnectStatusChecker);
          }

          if (resp.status === "failed") {
            setTimeout(async () => {
              await this.setupPaywall();
            }, 2000);

            this.stripeConnectProcessing = false;
            this.stripeConnectLinkError = true;
            clearInterval(this.stripeConnectStatusChecker);
          }
        } catch (e) {
          this.$notify({
            group: "error",
            text: err.message || "linking paywall failed",
          });
          clearInterval(this.stripeConnectStatusChecker);
        }
      }, 3000);
    },
    onStripeConnectCancelClick() {
      clearInterval(this.stripeConnectStatusChecker);
      this.stripeConnectProcessing = false;
    },
    humanDate(date) {
      return moment(new Date(date)).format("MMM DD, YYYY, [at] hh:mm A");
    },
  },
  beforeUnmount() {
    clearInterval(this.stripeConnectStatusChecker);
  },
};
</script>

<style scoped></style>
